/**
 * Feed — список вакансій за останні 7 днів у обраних користувачем сферах.
 *
 * UX:
 *   BackNav + hero «Вакансії»
 *   Cards з:
 *     - Категорія badge + час
 *     - AI summary (або preview якщо немає)
 *     - Рейтинг замовника (★ N.N · M відгуків) якщо є
 *   Infinite append кожні 20 + кнопка «Завантажити ще» (1.0 паритет UX)
 */

import { CornerBadge } from "@/components/CornerBadge";
import { useEffect, useMemo, useRef, useState } from "react";
import type React from "react";
import { createPortal } from "react-dom";
import { BackNav } from "@/components/BackNav";
import { FactPills } from "@/components/FactPills";
import { WorkKindBadge } from "@/components/WorkKindBadge";
import { PageHeader } from "@/components/PageHeader";
import { GiftBanner } from "@/pages/Subscription";
import { AuraBackground } from "@/components/AuraBackground";
import { StandaloneHeader } from "@/components/StandaloneHeader";
import { renderVacancyHtml } from "@/lib/vacancy_text";
import { openExternalOnAnchorClick, openTelegramUserLink } from "@/lib/openExternal";
import { getViewedVacancies, markVacancyViewed } from "@/lib/viewedVacancies";
import { stripHtml } from "@/lib/strip_html";

const SOURCE_LABEL: Record<string, string> = {
  telegram_channel: "TG-канал",
  telegram_chat: "TG-чат",
  threads: "Threads",
  freelancehunt: "Freelancehunt",
  finder: "inFLOW Finder",
  user_submitted: "Користувач",
};
import { Icon } from "@/components/Icon";
import { LockedSectionPopup } from "@/components/LockedSectionPopup";
import { TgEmoji } from "@/components/TgEmoji";
import { RemovedVacancyNotice } from "@/components/RemovedVacancyNotice";
import { CenteredError, Spinner } from "@/components/Spinner";
import { useNavigate } from "react-router-dom";
import {
  FeedUpsellBanner,
  useContactLimitPopup,
} from "@/components/SubscriptionUpsell";
import { RetentionOfferBanner } from "@/components/RetentionOffer";
import {
  fetchFeedPage,
  useCategories,
  useFeedFirstPage,
  useFeedVacancy,
  useFlagNotVacancy,
  useHideVacancy,
  useGiftWindow,
  useAdminRemoveVacancy,
  useMe,
  useMySubscription,
  fetchSearchPage,
  useProfileFull,
  usePromoCheck,
  useSubmitReview,
  useUnlockContacts,
  useToggleSaveVacancy,
  useUpdateProfileFull,
  useVacancyReviews,
  useAdminDeleteReview,
} from "@/api/hooks";
import type { SourcesState } from "@/api/hooks";
import { isActiveSubscription } from "@/api/types";
import type { FeedItemOut, ParsedContacts } from "@/api/hooks";
import { tg } from "@/lib/telegram";
import { filterContactLinks } from "@/lib/contactLinks";
import PostLinkRow from "@/components/PostLinkRow";
import { showAppAlert } from "@/lib/appAlert";
import { showReviewSaved } from "@/lib/reviewReward";
import { showToast } from "@/lib/toast";
import { AIResponseSheet } from "@/components/AIResponseSheet";

const FEED_LAST_SEEN_KEY = "feed:last_seen_at";

// Бейдж формату роботи (owner 2026-07-23). unspecified → без бейджа (щоб не
// засмічувати картку — таких вакансій багато). online/offline/hybrid — кольорові.
const WORK_FORMAT_BADGE: Record<string, { label: string; cls: string }> = {
  online: { label: "Онлайн", cls: "text-emerald-700 bg-emerald-100" },
  offline: { label: "Офлайн", cls: "text-blue-700 bg-blue-100" },
  hybrid: { label: "Гібрид", cls: "text-purple-700 bg-purple-100" },
};

function WorkFormatBadge({ fmt }: { fmt: string }) {
  const b = WORK_FORMAT_BADGE[fmt];
  if (!b) return null;
  return (
    <span
      className={
        "inline-block font-mono text-[0.625rem] tracking-[0.04em] px-2 py-0.5 rounded-full shrink-0 " +
        b.cls
      }
    >
      {b.label}
    </span>
  );
}

function relTime(iso: string): string {
  // Юзер 2026-05-25: «треба РЕАЛЬНИЙ час публікації, не «щойно». Якщо
  // у мене FREE з затримкою 4 год — вакансія опублікована 4 год тому,
  // а не «зараз». NEW бейдж означає що для МЕНЕ вакансія нова, але
  // публікація була раніше».
  // Формат: «сьогодні HH:MM» / «вчора HH:MM» / «DD.MM HH:MM».
  const d = new Date(iso);
  const now = new Date();
  const hhmm = d.toLocaleTimeString("uk-UA", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return `сьогодні ${hhmm}`;
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return `вчора ${hhmm}`;
  // Дальше — DD.MM HH:MM
  const ddmm = d.toLocaleDateString("uk-UA", {
    day: "2-digit",
    month: "2-digit",
  });
  return `${ddmm} ${hhmm}`;
}

// Ключ дня (локальний) для групування стрічки по днях.
function feedDayKey(iso: string): string {
  return new Date(iso).toDateString();
}

// Підпис розділювача дня: «Сьогодні» / «Вчора» / «12 червня»
// (Telegram-style, юзер 2026-06-13).
function feedDayLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return "Сьогодні";
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return "Вчора";
  return d.toLocaleDateString("uk-UA", { day: "numeric", month: "long" });
}

// Розділювач дня в стрічці — центрована плашка (як дата в чаті Telegram).
function DayDivider({ label }: { label: string }) {
  return (
    <div className="flex justify-center py-0.5">
      <span className="rounded-full bg-ink/[0.06] px-3 py-1 font-mono text-[0.65625rem] uppercase tracking-[0.08em] text-ink-dim">
        {label}
      </span>
    </div>
  );
}

function StarLine({ avg, count }: { avg: number; count: number }) {
  // Зірки — по фактичному avg (floor + півзірка). Підпис показуємо ТОЧНО,
  // без округлення: 4.96 → «4.96», а не «5.0» (юзер 2026-06-13: «5.0 а
  // зірок 4» — бо toFixed(1) округляв 4.96 до 5.0).
  const full = Math.floor(avg);
  const half = avg - full >= 0.5;
  const word = count === 1 ? "голос" : count < 5 ? "голоси" : "голосів";
  return (
    <div className="flex items-center gap-1 text-[0.6875rem]">
      <div className="flex">
        {[0, 1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className={
              i < full
                ? "text-primary"
                : i === full && half
                  ? "text-primary opacity-60"
                  : "text-ink/15"
            }
          >
            ★
          </span>
        ))}
      </div>
      <span className="font-mono text-ink-dim">
        {avg.toFixed(2).replace(/0$/, "")} · {count} {word}
      </span>
    </div>
  );
}

// Делегуємо в openTelegramUserLink: мобільні → openTelegramLink, ПК/веб →
// openLink (на десктопі openTelegramLink закривав апку — юзер 2026-06-11).
function openTgLink(url: string) {
  openTelegramUserLink(url);
}

type UrlClassification = {
  label: string;
  icon: import("@/components/Icon").IconName;
  tone: "primary" | "emerald" | "blue" | "pink" | "purple" | "ink";
};

function classifyUrl(url: string): UrlClassification {
  const u = url.toLowerCase();
  if (u.includes("wa.me/") || u.includes("whatsapp.com"))
    return { label: "WhatsApp", icon: "whatsapp", tone: "emerald" };
  if (u.startsWith("viber://") || u.includes("viber"))
    return { label: "Viber", icon: "chat-call", tone: "purple" };
  if (u.includes("threads.net") || u.includes("threads.com"))
    return { label: "Посилання на вакансію", icon: "threads", tone: "ink" };
  if (u.includes("instagram.com"))
    return { label: "Інстаграм замовника", icon: "instagram", tone: "pink" };
  // Юзер 2026-05-23: «у вакансіях з Freelancehunt замість "Посилання"
  // має бути "Посилання на вакансію"» — окрема гілка ПЕРЕД generic fallback.
  if (u.includes("freelancehunt.com"))
    return { label: "Посилання на вакансію", icon: "project", tone: "primary" };
  if (u.includes("facebook.com") || u.includes("fb.com"))
    return { label: "Facebook", icon: "facebook", tone: "blue" };
  if (u.includes("linkedin.com"))
    return { label: "LinkedIn", icon: "linkedin", tone: "blue" };
  if (u.includes("docs.google.com") || u.includes("forms.gle"))
    return { label: "Google Forms", icon: "doc", tone: "primary" };
  if (
    u.includes("tally.so") ||
    u.includes("typeform.com") ||
    u.includes("notion.so") ||
    u.includes("airtable.com")
  )
    return { label: "Форма", icon: "form", tone: "primary" };
  return { label: "Посилання", icon: "globe", tone: "primary" };
}

function toneClasses(tone: UrlClassification["tone"]): {
  bg: string;
  text: string;
} {
  switch (tone) {
    case "emerald":
      return { bg: "bg-emerald-100", text: "text-emerald-700" };
    case "blue":
      return { bg: "bg-blue-100", text: "text-blue-700" };
    case "pink":
      return { bg: "bg-pink-100", text: "text-pink-600" };
    case "purple":
      return { bg: "bg-purple-100", text: "text-purple-700" };
    case "ink":
      return { bg: "bg-ink/10", text: "text-ink" };
    case "primary":
    default:
      return { bg: "bg-primary/10", text: "text-primary" };
  }
}

/**
 * ContactsList — 1:1 з FeedVacancy.tsx (двохрядковий формат label/value).
 * Юзер 2026-05-23: «вид вакансій по кнопці Відкрити вакансію відрізняється
 * від вигляду в стрічці» — раніше Feed.tsx мав власну стару верстку.
 */
function ContactsList({
  contacts,
  postUrl,
}: {
  contacts: ParsedContacts;
  postUrl?: string | null;
}) {
  // Юзернейм може прийти і з «@», і без. Малюємо «собачку» самі,
  // тому чужу спершу знімаємо — інакше «@@nick» (owner 2026-09-08).
  const strip = (u: string) => u.replace(/^@+/, "");
  const [primaryTg, ...secondaryTg] = contacts.telegrams.map(strip);
  // Юзер 2026-05-23: «вакансія де двічі юзернейм написаний» — раніше
  // fallback `?? @${primaryTg}` робив, що великий рядок і малий рядок
  // ставали ОДНАКОВИМИ. Тепер: якщо реального Name немає → показуємо
  // ОДИН рядок (@username великий), без duplicated subtitle.
  const primaryName = primaryTg ? contacts.tg_names?.[primaryTg] ?? null : null;

  return (
    <div className="space-y-3">
      {/* Primary TG — CTA з Name / @username */}
      {primaryTg && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openTgLink(`https://t.me/${primaryTg}`);
          }}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-primary text-white shadow-card active:bg-primary-deep transition"
        >
          <div className="grid h-9 w-9 place-items-center rounded-full bg-white/20 text-white shrink-0">
            <Icon name="telegram" size={16} color="#FFFFFF" />
          </div>
          <div className="flex-1 min-w-0 text-left">
            {primaryName ? (
              <>
                <div className="font-display text-[0.875rem] font-bold text-white truncate">
                  {primaryName}
                </div>
                <div className="font-mono text-[0.75rem] text-white/80 mt-0.5 truncate">
                  @{primaryTg}
                </div>
              </>
            ) : (
              <div className="font-display text-[0.875rem] font-bold text-white truncate">
                @{primaryTg}
              </div>
            )}
          </div>
          <Icon name="arrow-right" size={16} color="#FFFFFF" className="-rotate-45" />
        </button>
      )}

      {/* Інші TG — компактні з тим самим Name/username layout */}
      {secondaryTg.length > 0 && (
        <div className="space-y-1.5">
          {secondaryTg.map((u) => {
            const name = contacts.tg_names?.[u];
            return (
              <button
                key={u}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openTgLink(`https://t.me/${u}`);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border border-ink/[0.08] active:bg-surface-soft transition text-left"
              >
                <div className="grid h-7 w-7 place-items-center rounded-full bg-primary/10 text-primary shrink-0">
                  <Icon name="telegram" size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  {name && (
                    <div className="font-display text-[0.78125rem] font-semibold text-ink truncate">
                      {name}
                    </div>
                  )}
                  <div className="font-mono text-[0.71875rem] text-ink-dim truncate">
                    @{u.replace(/^@+/, "")}
                  </div>
                </div>
                <Icon name="arrow-right" size={12} color="rgba(254,107,4,0.85)" className="-rotate-45" />
              </button>
            );
          })}
        </div>
      )}

      {/* Phone + Email */}
      {(contacts.phones.length > 0 || contacts.emails.length > 0) && (
        <div className="space-y-1.5">
          {contacts.phones.map((p) => (
            <a
              key={p}
              href={`tel:${p}`}
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-2.5 px-3 py-2 rounded-xl border border-ink/[0.08] active:bg-surface-soft transition"
            >
              <div className="grid h-7 w-7 place-items-center rounded-full bg-emerald-100 text-emerald-700 shrink-0">
                <Icon name="phone" size={14} />
              </div>
              <div className="flex-1 font-mono text-[0.78125rem] text-ink truncate">{p}</div>
              <Icon name="arrow-right" size={12} color="rgba(5,150,105,0.75)" className="-rotate-45" />
            </a>
          ))}
          {(contacts.emails || []).map((email) => (
            <a
              key={email}
              href={`mailto:${email}`}
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-2.5 px-3 py-2 rounded-xl border border-ink/[0.08] active:bg-surface-soft transition"
            >
              <div className="grid h-7 w-7 place-items-center rounded-full bg-blue-100 text-blue-700 shrink-0">
                <Icon name="mail" size={14} />
              </div>
              <div className="flex-1 font-mono text-[0.78125rem] text-ink truncate">{email}</div>
              <Icon name="arrow-right" size={12} color="rgba(29,78,216,0.75)" className="-rotate-45" />
            </a>
          ))}
        </div>
      )}

      {/* URLs — двохрядковий label/value з brand-icon-circle */}
      {contacts.urls.length > 0 && (
        <div className="space-y-1.5">
          {contacts.urls.map((u) => {
            const c = classifyUrl(u);
            const tone = toneClasses(c.tone);
            return (
              <button
                key={u}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openTgLink(u);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border border-ink/[0.08] active:bg-surface-soft transition text-left"
              >
                <div className={"grid h-7 w-7 place-items-center rounded-full shrink-0 " + tone.bg + " " + tone.text}>
                  <Icon name={c.icon} size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  {/* Юзер 2026-05-23: «Посилання на вакансію» / «Інстаграм
                      замовника» не повинні переноситись на 2 рядки навіть
                      на малих пристроях. nowrap + truncate. */}
                  <div className="font-display text-[0.75rem] font-semibold text-ink whitespace-nowrap truncate">
                    {c.label}
                  </div>
                  <div className="font-mono text-[0.65625rem] text-ink-dim truncate">{u}</div>
                </div>
                <Icon name="arrow-right" size={12} color="rgba(254,107,4,0.85)" className="-rotate-45" />
              </button>
            );
          })}
        </div>
      )}
      {postUrl && <PostLinkRow url={postUrl} />}
    </div>
  );
}

function ExpandedDetail({ vacancyId }: { vacancyId: number }) {
  const q = useFeedVacancy(vacancyId);
  const unlock = useUnlockContacts(vacancyId);
  const notVac = useFlagNotVacancy(vacancyId);
  const hideVac = useHideVacancy(vacancyId);
  const adminRemove = useAdminRemoveVacancy(vacancyId);
  const toggleSave = useToggleSaveVacancy(vacancyId);
  // Збереження — функція для підписників (owner 2026-09-08).
  const subQ = useMySubscription();
  const isPaidUser = subQ.data ? isActiveSubscription(subQ.data) : false;
  // Замкнена кнопка «Зберегти» відкриває той самий попап, що й замкнені
  // рядки розділів на Головній (owner 2026-09-12).
  const [savedLocked, setSavedLocked] = useState(false);
  const meQ = useMe();
  const navigate = useNavigate();
  // Попап про тижневий ліміт контактів (owner 2026-09-08): відповідаємо там,
  // де сталась дія. Лист у чат лишається за ботом.
  const limitPopup = useContactLimitPopup();

  // АДМІН: зняти вакансію глобально (owner 2026-08-06) — зі стрічки у всіх і
  // з їхніх чатів у боті. Незворотно, тому через підтвердження.
  const handleAdminRemove = () => {
    if (adminRemove.isPending) return;
    tg()?.HapticFeedback?.impactOccurred?.("medium");
    showAppAlert(
      "Вакансія зникне зі стрічки у всіх і відкриватиметься як знята, без контактів. Повідомлення в ботах видаляться, якщо їм менше кількох годин. Дію не відкотити.",
      {
        title: "Видалити вакансію?",
        // Попап = тільки анімоване емодзі (правило проєкту): кошика в
        // наборі нема, дію «видалити назавжди» передає ❌.
        emoji: "❌",
        buttons: [
          {
            label: "Видалити",
            variant: "danger",
            onClick: () =>
              adminRemove.mutate(undefined, {
                onSuccess: () => {
                  tg()?.HapticFeedback?.notificationOccurred?.("success");
                  showToast("Вакансію знято", { emoji: "✓" });
                },
                onError: (e) =>
                  showAppAlert((e as Error).message, { title: "Помилка" }),
              }),
          },
          { label: "Скасувати", variant: "ghost" },
        ],
      },
    );
  };

  const handleDeleteVacancy = () => {
    if (hideVac.isPending) return;
    tg()?.HapticFeedback?.impactOccurred?.("light");
    hideVac.mutate(undefined, {
      onSuccess: () => {
        tg()?.HapticFeedback?.notificationOccurred?.("success");
        showToast("Прибрали 🌤", { emoji: "🗑" });
      },
    });
  };

  const handleNotVacancy = () => {
    if (notVac.isPending) return;
    tg()?.HapticFeedback?.impactOccurred?.("light");
    showAppAlert("Це не вакансія / спам? Прибрати її зі стрічки?", {
      title: "Це не вакансія",
      emoji: "❌",
      buttons: [
        {
          label: "Так, прибрати",
          variant: "danger",
          onClick: () =>
            notVac.mutate(undefined, {
              onSuccess: () => {
                tg()?.HapticFeedback?.notificationOccurred?.("success");
                showToast("Прибрали цю вакансію", { emoji: "✓" });
              },
              onError: (e) =>
                showAppAlert((e as Error).message, { title: "Помилка" }),
            }),
        },
        { label: "Скасувати", variant: "ghost" },
      ],
    });
  };
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const reviewsQ = useVacancyReviews(vacancyId, reviewsOpen);
  const adminDeleteReview = useAdminDeleteReview(vacancyId);
  const submitReview = useSubmitReview(vacancyId);
  const [myRating, setMyRating] = useState<number>(0);
  const [reviewText, setReviewText] = useState<string>("");
  const navigateToSubscription = () => navigate("/subscription");

  useEffect(() => {
    if (q.data?.my_rating) setMyRating(q.data.my_rating);
  }, [q.data?.my_rating]);

  if (q.isLoading) return <div className="px-4 pb-4"><Spinner /></div>;
  if (q.isError) return <div className="px-4 pb-4 text-[0.75rem] text-red-500">{q.error.message}</div>;
  const v = q.data!;
  // Знята вакансія (owner 2026-09-25): замість тексту, контактів і кнопок —
  // лише пояснення. Сервер у цьому стані їх і не віддає.
  if (v.removed) {
    return (
      <div className="px-4 pb-4">
        <RemovedVacancyNotice compact />
      </div>
    );
  }
  // Юзер 2026-05-23: «під вакансіями з юзернеймом нема інтерфейсу
  // відгуків». Відгуки/рейтинг — ТІЛЬКИ під TG-чат вакансіями (там
  // парсер вже вийняв username замовника зі структурного «Контакт: ...»).
  // Для інших sources (threads/freelancehunt/telegram_channel) fallback
  // НЕ застосовуємо: contacts_parsed.telegrams[] може містити випадковий
  // @юзернейм зі згадки в тексті, не клієнта (code-reviewer C2).
  const effectiveContactUsername =
    v.contact_username ??
    (v.source === "telegram_chat" ? v.contacts.telegrams[0] ?? null : null);
  const canRate = effectiveContactUsername !== null && v.contacts_unlocked;

  return (
    <div className="px-4 pb-4 space-y-3">
      {limitPopup.node}
      {/* Full text */}
      <div className="rounded-xl bg-bg/40 border border-ink/[0.06] p-3">
        <div className="font-mono text-[0.625rem] uppercase tracking-[0.1em] text-ink-dim mb-2">Текст вакансії</div>
        <div
          className="text-[0.84375rem] leading-relaxed text-ink whitespace-pre-wrap break-words [overflow-wrap:anywhere] [&_b]:font-semibold [&_strong]:font-semibold [&_u]:underline [&_i]:italic [&_em]:italic [&_a]:text-primary [&_a]:underline"
          onClick={openExternalOnAnchorClick}
          dangerouslySetInnerHTML={{
            // Текст із посиланнями всередині, якщо парсер його прислав
              // (owner 2026-08-31).
              __html: renderVacancyHtml(
                v.text_html || v.text,
                v.contacts_unlocked,
              ),
          }}
        />
      </div>

      {/* Contacts — об'єднуємо contact_username (з intake) + contacts_parsed.
          Іноді username є у vacancy.contact_username, але парсер тексту не
          витяг його у contacts_parsed (наприклад текст не містив @prefix). */}
      {(() => {
        // contact_username — це юзернейм замовника, але НЕ завжди
        // телеграмний: у вакансій із Threads там лежить хендл Threads
        // (owner 2026-08-30: «натискаю телеграм — такого користувача не
        // існує, воно додало вигаданий username»). Додаємо його в телеграми
        // лише коли замовник справді з Telegram.
        const tgCustomer =
          (v.customer_platform ?? "telegram") === "telegram" &&
          v.source !== "threads";
        const mergedTelegrams = Array.from(new Set([
          ...(v.contacts.telegrams || []),
          ...(tgCustomer && v.contact_username ? [v.contact_username] : []),
        ]));
        // Посилання на пост у групі — контакт, коли іншого немає (owner
        // 2026-09-24). Посилання t.me самі по собі контактом не рахуємо
        // (owner 2026-08-31) — інакше блок казав «є», а список був порожній.
        const postUrl = v.post_url ?? null;
        const visibleUrls = filterContactLinks(v.contacts.urls);
        const hasAnyContact =
          mergedTelegrams.length > 0 ||
          v.contacts.phones.length > 0 ||
          v.contacts.emails.length > 0 ||
          visibleUrls.length > 0 ||
          !!postUrl;
        const mergedContacts: ParsedContacts = {
          telegrams: mergedTelegrams,
          phones: v.contacts.phones,
          emails: v.contacts.emails,
          // Інвайт у канал («Приєднуйтесь») — реклама джерела, а не контакт
          // замовника (owner 2026-08-31).
          urls: visibleUrls,
          // Передаємо tg_names щоб Feed inline-expand показав
          // «Name» зверху / «@username» знизу (юзер 2026-05-23: «у стрічці
          // дублюється @username замість Name + @username»).
          tg_names: v.contacts.tg_names || {},
        };
        return (
      <div className="rounded-ios-card bg-surface shadow-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          <span className="font-mono text-[0.625rem] uppercase tracking-[0.12em] text-ink-dim">
            Контакти замовника
          </span>
          {v.contacts_unlocked && hasAnyContact && (
            <span className="ml-auto font-mono text-[0.625rem] text-ink-faint">
              {mergedTelegrams.length +
                v.contacts.phones.length +
                v.contacts.emails.length +
                visibleUrls.length +
                (postUrl ? 1 : 0)}{" "}
              шт.
            </span>
          )}
        </div>
        {!hasAnyContact ? (
          <div className="rounded-xl border border-dashed border-ink/15 py-5 px-4 text-center">
            <div className="text-[1.25rem] mb-1">🔍</div>
            <div className="text-[0.75rem] text-ink-dim leading-snug">
              У цій вакансії немає прямих контактів.
            </div>
          </div>
        ) : v.contacts_unlocked ? (
          <ContactsList contacts={mergedContacts} postUrl={postUrl} />
        ) : (
          <div className="relative rounded-xl overflow-hidden bg-bg/40 border border-ink/10 p-4">
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-4" style={{ filter: "blur(6px)" }}>
              <span className="font-mono text-[0.875rem] text-ink">@{(mergedTelegrams[0] || "hidden").replace(/^@+/, "")}</span>
            </div>
            <div className="relative z-10 flex flex-col items-center text-center py-3">
              <div className="grid h-12 w-12 place-items-center rounded-2xl bg-primary/10 mb-2.5">
                <Icon name="lock" size={22} color="#FE6B04" />
              </div>
              <div className="font-display text-[0.875rem] font-bold text-ink mb-1">Контакти приховано</div>
              <div className="text-[0.71875rem] text-ink-dim mb-3.5 leading-snug max-w-[15rem]">Відкрий, щоб написати замовнику напряму</div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (v.attempts_left_today === 0) {
                    // Прямой переход на /subscription (юзер хочет цей flow)
                    navigateToSubscription();
                  } else {
                    unlock.mutate(undefined, {
                      onSuccess: (res) => limitPopup.check(res),
                    });
                  }
                }}
                disabled={unlock.isPending}
                className={
                  "inline-flex items-center justify-center gap-1.5 rounded-full px-5 py-2 font-display text-[0.8125rem] font-semibold transition whitespace-nowrap " +
                  (unlock.isPending
                    ? "bg-ink/10 text-ink-dim cursor-not-allowed"
                    : v.attempts_left_today === 0
                      ? "bg-dark text-white active:bg-ink shadow-card-soft"
                      : "bg-primary text-white active:bg-primary-deep shadow-card-soft")
                }
              >
                {unlock.isPending ? (
                  "Відкриваю..."
                ) : v.attempts_left_today === 0 ? (
                  <>
                    <TgEmoji char="🔒" size={16} />
                    Зняти ліміт
                  </>
                ) : (
                  <>
                    <TgEmoji char="🔑" size={16} />
                    Відкрити ({v.attempts_left_today}/5)
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
      );
      })()}

      {/* AI helper — генерація відгуку на вакансію */}
      <AIHelperStub vacancyId={vacancyId} contactUsername={effectiveContactUsername} />

      {/* «Зберегти» — той самий ряд, що й у боті та на сторінці вакансії.
          Owner 2026-09-12: безкоштовним кнопку НЕ ховаємо — сіра й під
          замком. Правило на всю аппку: оформлення однакове, закрите просто
          сіре із замком. */}
      {v.contacts_unlocked !== undefined && (
        <button
          type="button"
          onClick={() => {
            tg()?.HapticFeedback?.impactOccurred?.("light");
            if (!isPaidUser) {
              setSavedLocked(true);
              return;
            }
            toggleSave.mutate(undefined, {
              onSuccess: (r) =>
                showToast(r.saved ? "Збережено" : "Прибрали зі збережених", {
                  emoji: r.saved ? "🔖" : "✓",
                }),
              onError: (e) => showAppAlert((e as Error).message),
            });
          }}
          disabled={toggleSave.isPending}
          className={
            "flex w-full items-center justify-center gap-2 rounded-full py-2.5 " +
            "font-display text-[0.78125rem] font-semibold transition " +
            "active:scale-[0.98] disabled:opacity-50 " +
            (!isPaidUser
              ? "bg-ink/[0.04] border border-ink/[0.08] text-ink-faint"
              : v.is_saved
                ? "bg-primary/10 border border-primary/30 text-primary"
                : "bg-surface-soft border border-ink/[0.08] text-ink-dim")
          }
        >
          <Icon
            name={v.is_saved && isPaidUser ? "bookmark-fill" : "bookmark"}
            size={16}
            color={!isPaidUser ? "#A8A29C" : undefined}
          />
          {v.is_saved && isPaidUser ? "Збережено" : "Зберегти"}
          {!isPaidUser && <Icon name="lock" size={14} color="#A8A29C" />}
        </button>
      )}

      <LockedSectionPopup
        sectionKey={savedLocked ? "saved" : null}
        open={savedLocked}
        onClose={() => setSavedLocked(false)}
      />

      {/* «Це не вакансія» — скарга (юзер 2026-06-25). У стрічці inline-розворот
          теж має кнопку (не лише окрема сторінка вакансії). */}
      {/* Один ряд: скарга і «прибрати в себе» (owner 2026-08-15). «Видалити»
          ширша — це дія, якою користуються частіше. */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleNotVacancy}
          disabled={notVac.isPending}
          className="flex shrink-0 items-center justify-center gap-2 rounded-full bg-surface-soft border border-danger/20 px-4 py-2.5 font-display text-[0.78125rem] font-semibold text-danger whitespace-nowrap active:scale-[0.98] transition disabled:opacity-50"
        >
          <Icon name="flag" size={16} />
          Не вакансія
        </button>
        <button
          type="button"
          onClick={handleDeleteVacancy}
          disabled={hideVac.isPending}
          className="flex flex-1 items-center justify-center gap-2 rounded-full bg-surface-soft border border-ink/[0.08] py-2.5 font-display text-[0.78125rem] font-semibold text-ink-dim whitespace-nowrap active:scale-[0.98] transition disabled:opacity-50"
        >
          <Icon name="trash" size={16} />
          Видалити
        </button>
      </div>

      {/* АДМІН-кнопка: видалити вакансію у всіх (owner 2026-08-06) */}
      {meQ.data?.is_admin && (
        <button
          type="button"
          onClick={handleAdminRemove}
          disabled={adminRemove.isPending}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-danger/10 border border-danger/30 py-2.5 font-display text-[0.78125rem] font-semibold text-danger active:scale-[0.98] transition disabled:opacity-50"
        >
          <Icon name="trash" size={16} />
          {adminRemove.isPending ? "Видаляю..." : "Видалити вакансію (адмін)"}
        </button>
      )}

      {/* Reviews about client (collapsible) */}
      {effectiveContactUsername && (
        <div className="rounded-xl bg-bg/40 border border-ink/[0.06]">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setReviewsOpen((x) => !x); }}
            className="w-full flex items-center gap-3 px-3 py-3 text-left"
          >
            {/* Юзер 2026-05-23: «рейтинг и количество голосів — на другій строці,
                а не в один рядок». Рядок 1 = «Відгуки про @username».
                Рядок 2 = три ЗАВЖДИ-присутні мітки: «★ 4.5 · 3 голоси · 5 відгуків».
                Reviews count тепер з v.client_reviews_count (без fetch'у). */}
            <div className="flex-1 min-w-0">
              {v.client_rating_count >= 10 && (v.client_rating_avg ?? 0) >= 4.8 && (
                <div className="-ml-2 mb-1 inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[0.625rem] font-display font-semibold text-emerald-600">
                  <TgEmoji char="✅" size={13} />
                  Надійний замовник
                </div>
              )}
              <div className="font-mono text-[0.625rem] uppercase tracking-[0.1em] text-ink-dim truncate">
                {v.contacts_unlocked
                  ? `Відгуки про @${effectiveContactUsername}`
                  : "Відгуки про клієнта"}
              </div>
              {(() => {
                const rc = v.client_rating_count;
                const reviewsN = v.client_reviews_count;
                // Українська плюралізація (1 голос / 2-4 голоси / 5+ голосів)
                const votesWord = rc === 1 ? "голос" : rc >= 2 && rc <= 4 ? "голоси" : "голосів";
                const reviewsWord = reviewsN === 1 ? "відгук" : reviewsN >= 2 && reviewsN <= 4 ? "відгуки" : "відгуків";
                const parts: string[] = [];
                if (rc > 0) {
                  parts.push(`★ ${v.client_rating_avg != null ? v.client_rating_avg.toFixed(2).replace(/0$/, "") : "—"}`);
                  parts.push(`${rc} ${votesWord}`);
                }
                // Відгуки — ЗАВЖДИ показуємо (включно з «0 відгуків»),
                // юзер: «нуль відгуків або один відгук два відгуки…».
                parts.push(`${reviewsN} ${reviewsWord}`);
                return (
                  <div className="mt-0.5 text-[0.6875rem] text-ink truncate">
                    {parts.join(" · ")}
                  </div>
                );
              })()}
            </div>
            <Icon name="chevron" size={12} color="rgba(23,20,17,0.45)" className={"text-ink-dim transition-transform duration-200 shrink-0 " + (reviewsOpen ? "rotate-180" : "")} />
          </button>
          <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${reviewsOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
            <div className="overflow-hidden">
              <div className="px-3 pb-3">
                {reviewsQ.isLoading && <Spinner />}
                {/* Відгук = ТІЛЬКИ текст (юзер 2026-06-11): зірки без
                    коментаря у списку не показуємо. */}
                {(() => {
                  const textReviews =
                    reviewsQ.data?.items.filter(
                      (r) => r.review_text && r.review_text.trim(),
                    ) ?? [];
                  if (reviewsQ.data && textReviews.length === 0) {
                    return <div className="text-[0.75rem] text-ink-dim text-center py-2">Відгуків ще немає</div>;
                  }
                  return textReviews.map((r) => (
                    <div
                      key={r.id}
                      className="mb-2 rounded-xl border border-ink/[0.07] bg-bg/70 p-2.5 last:mb-0"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex">
                          {[1,2,3,4,5].map(i => (<span key={i} className={i <= r.rating ? "text-primary" : "text-ink/15"}>★</span>))}
                        </div>
                        {/* Відгуки анонімні (owner 2026-08-24): хто писав —
                            не показуємо ніде. */}
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[0.625rem] text-ink-dim">
                            Анонімно
                          </span>
                          {/* Адмін прибирає наклеп і помсту (owner 2026-08-31).
                              Звичайна людина цієї кнопки не бачить. */}
                          {meQ.data?.is_admin && (
                            <button
                              type="button"
                              aria-label="Видалити відгук"
                              onClick={() => {
                                if (!window.confirm("Видалити цей відгук?")) return;
                                adminDeleteReview.mutate(r.id);
                              }}
                              disabled={adminDeleteReview.isPending}
                              className="grid h-6 w-6 place-items-center rounded-full text-danger active:scale-90 transition disabled:opacity-40"
                            >
                              <Icon name="trash" size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="text-[0.78125rem] leading-relaxed text-ink mt-1">{r.review_text}</p>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rate */}
      {effectiveContactUsername && canRate && (
        <div className="rounded-xl bg-bg/40 border border-ink/[0.06] p-3">
          <div className="font-mono text-[0.625rem] uppercase tracking-[0.1em] text-ink-dim mb-2">Твоя оцінка</div>
          <div className="flex gap-1 mb-2">
            {[1,2,3,4,5].map(i => (
              <button
                key={i}
                type="button"
                onClick={(e) => { e.stopPropagation(); setMyRating(i); }}
                className={"text-[1.375rem] leading-none active:scale-90 transition " + (i <= myRating ? "text-primary" : "text-ink/20")}
              >★</button>
            ))}
          </div>
          <textarea
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            rows={2}
            maxLength={2000}
            placeholder={
              myRating >= 1 && myRating <= 3
                ? "Поясни оцінку — що пішло не так (обовʼязково)"
                : "Кілька слів про клієнта (опційно)"
            }
            /* Те саме поле, що й на сторінці вакансії (owner 2026-09-17). */
            className="w-full resize-none rounded-lg border border-ink/[0.12] bg-bg px-2.5 py-2 text-[0.78125rem] leading-relaxed text-ink placeholder:text-ink-faint focus:border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          {myRating >= 1 && myRating <= 3 && !reviewText.trim() && (
            <div className="mt-1 text-[0.6875rem] text-primary">
              Для оцінки 1-3 зірки коментар обовʼязковий.
            </div>
          )}
          <button
            type="button"
            onClick={async (e) => {
              e.stopPropagation();
              if (myRating < 1) { showAppAlert("Постав від 1 до 5 ★"); return; }
              if (myRating <= 3 && !reviewText.trim()) {
                showAppAlert("Для оцінки 1-3 зірки додай коментар — поясни, що не так.");
                return;
              }
              try {
                const res = await submitReview.mutateAsync({ rating: myRating, review_text: reviewText.trim() || undefined });
                tg()?.HapticFeedback?.notificationOccurred?.("success");
                showReviewSaved(res, () => navigate("/quests"));
                setReviewText("");
              } catch (err) {
                tg()?.HapticFeedback?.notificationOccurred?.("error");
                showAppAlert((err as Error).message);
              }
            }}
            disabled={submitReview.isPending || myRating < 1 || (myRating <= 3 && !reviewText.trim())}
            className={
              "mt-2 block w-full rounded-full py-2.5 font-display text-[0.78125rem] font-semibold transition " +
              (submitReview.isPending || myRating < 1 || (myRating <= 3 && !reviewText.trim())
                ? "bg-ink/10 text-ink-dim cursor-not-allowed"
                : "bg-primary text-white active:bg-primary-deep")
            }
          >
            {submitReview.isPending ? "Зберігаю..." : v.my_rating ? "Оновити відгук" : "Залишити відгук"}
          </button>
          <p className="mt-2 text-center text-[0.75rem] leading-snug text-ink-dim [text-wrap:balance]">
            За кожну оцінку нараховуємо 1 ₴ на бонусний рахунок
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * FrozenFeedBanner — інфо-блок коли підписка заморожена.
 * Юзер 2026-05-22: «коли заморозив підписку — не отримую сповіщення
 * І не бачу нових вакансій у feed; зроби красивий блок з інфо».
 */
function FrozenFeedBanner({ sub }: { sub: { ends_at: string; days_left: number } }) {
  // ends_at == дата кінця підписки. До freeze ще треба показати дату закінчення
  // freeze. У нас немає окремого поля — використовуємо days_left як approximate
  // (бо ends_at вже зсувається при unfreeze).
  return (
    <div className="mx-5 mt-3">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200/50 p-4">
        <div
          className="pointer-events-none absolute -top-6 -right-6 h-[7.5rem] w-[7.5rem] rounded-full opacity-50"
          style={{
            background:
              "radial-gradient(circle, rgba(59,130,246,0.20), rgba(59,130,246,0))",
          }}
        />
        <div className="relative flex items-start gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-[0.75rem] bg-blue-500 shrink-0 shadow-card-soft">
            <span className="text-white text-[1.125rem]">❄️</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[0.625rem] uppercase tracking-[0.1em] text-blue-900/55">
              Підписка заморожена
            </div>
            <div className="font-display text-[0.9375rem] font-bold text-blue-950 mt-0.5 leading-tight">
              Отримання вакансій призупинено
            </div>
            <div className="text-[0.75rem] text-blue-900/75 mt-1.5 leading-snug">
              Нові вакансії не показуються та не приходять у бот на час
              заморозки. Підписка автоматично розмерзнеться — і ти знову
              отримуватимеш вакансії з того ж місця.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// AI helper stub — кнопка генерации отклика (UI готов, бэкенд позже).
// Экспортируется — використовується і в Feed inline-expand, і на окремій
// сторінці вакансії FeedVacancy (юзер 2026-06-11: «на сторінці вакансії
// кнопки генерації нема»).
export function AIHelperStub({
  vacancyId,
  contactUsername,
}: {
  vacancyId: number;
  contactUsername?: string | null;
}) {
  const subQuery = useMySubscription();
  const sub = subQuery.data;
  const isActive = sub && isActiveSubscription(sub);
  // Доступно з будь-якою активною підпискою (test+); FREE — під замком.
  // Ліміти (test 5/добу) гейтить бекенд при генерації.
  const canUse = isActive;
  // Діплінк із бота: /feed/:id?ai=style одразу відкриває форму свого стилю.
  const wantStyleForm =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("ai") === "style";
  const [sheetOpen, setSheetOpen] = useState(false);
  // Підписка вантажиться асинхронно, тому на першому рендері `canUse` ще
  // false — відкриваємо шторку, щойно стане відомо, що доступ є
  // (owner 2026-08-31: «кнопка просто вакансію відкриває»).
  useEffect(() => {
    if (wantStyleForm && canUse) setSheetOpen(true);
  }, [wantStyleForm, canUse]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canUse) {
      tg()?.HapticFeedback?.notificationOccurred?.("warning");
      showAppAlert(
        "Оформи підписку у розділі «Підписка», щоб користуватись AI-помічником для відгуків.",
        { title: "AI-помічник під замком", emoji: "🔒" },
      );
      return;
    }
    tg()?.HapticFeedback?.selectionChanged?.();
    setSheetOpen(true);
  };

  return (
    <>
    <button
      type="button"
      onClick={handleClick}
      className={
        "w-full flex items-center gap-3 px-3 py-3 rounded-xl border transition active:scale-[0.99] " +
        (canUse
          ? "bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20"
          : "bg-bg/40 border-ink/[0.06]")
      }
    >
      <div
        className={
          "grid h-10 w-10 place-items-center rounded-[0.75rem] shrink-0 " +
          (canUse ? "bg-primary text-white" : "bg-ink/10 text-ink-dim")
        }
      >
        {/* Owner 2026-09-11: тут стояв «бот» (чат із кодом) — «якого хуя
            іконка коду?». AI-відгук має свою іконку, обрану 11.09. */}
        <Icon name="brain" size={16} />
      </div>
      <div className="flex-1 min-w-0 text-left">
        <div className="font-display text-[0.8125rem] font-semibold text-ink truncate">
          Згенерувати відповідь
        </div>
        {/* Юзер 2026-05-23: «прибрати «цю», підзаголовок одна строка».
            «AI напише персональну відповідь на вакансію» — 43 chars, при
            text-[0.6875rem] + min-w-0 контейнері спокійно влазить в один
            рядок на стандартних mobile screens (375-414px). truncate
            як safety-net якщо клієнт-пристрій сильно вузький. */}
        <div className="text-[0.6875rem] text-ink-dim mt-0.5 leading-snug truncate">
          {canUse
            ? "AI напише персональну відповідь на вакансію"
            : "Доступно з підпискою"}
        </div>
      </div>
      {!canUse && (
        <Icon name="lock" size={14} color="#A8A29C" />
      )}
    </button>
    {sheetOpen && (
      <AIResponseSheet
        vacancyId={vacancyId}
        contactUsername={contactUsername}
        openStyleForm={wantStyleForm}
        onClose={() => setSheetOpen(false)}
      />
    )}
    </>
  );
}

export function FeedCard({
  item,
  isNew,
  viewed,
  expanded,
  onToggle,
  registerRef,
}: {
  item: FeedItemOut;
  isNew: boolean;
  viewed: boolean;
  expanded: boolean;
  onToggle: () => void;
  registerRef?: (el: HTMLDivElement | null) => void;
}) {
  // AI-summary інколи генерує з <b>...</b> всередині — strip усі теги
  // щоб не показувалося як plain text.
  const summary = stripHtml(item.ai_summary || item.text_preview);
  // Юзер 2026-05-23: «NEW таблетка має виходити за грань прямокутника
  // вакансії — частина всередині, частина зовні». Тому wrapper-div
  // БЕЗ overflow-hidden (щоб badge міг стирчати з кута), а внутрішня
  // картка з overflow-hidden зберігає радіус кутів для active-фону.
  return (
    <div
      ref={registerRef}
      className="relative scroll-mt-3"
    >
      {/* Переглянуто (юзер 2026-06-11) має пріоритет над new: щойно
          відкрив/розгорнув — картка стає «переглянутою» (сірий бейдж +
          затемнення), а не «new». */}
      {viewed ? (
        <CornerBadge tone="muted">переглянуто</CornerBadge>
      ) : isNew ? (
        <CornerBadge>new</CornerBadge>
      ) : null}
      <div
        className={
          "rounded-ios-card overflow-hidden transition " +
          (viewed
            ? "bg-surface-soft shadow-card-soft"
            : "bg-surface shadow-card " + (isNew ? "ring-1 ring-primary/40" : ""))
        }
      >
      <button
        type="button"
        onClick={onToggle}
        className="w-full block text-left active:bg-surface-soft transition p-4"
      >
        {/* Header — category (truncate) + source (shrink-0) + time (shrink-0).
            БЕЗ flex-wrap — інакше при довгій категорії time переноситься
            (юзер 2026-05-23: «время переноситься і виглядає не дуже»).
            min-w-0 + truncate на category — щоб саме категорія обрізалась
            трикрапкою, а source+time лишались на тій же лінії. */}
        {/* Шапка (owner 2026-07-23): формат + категорія + джерело. Час
            перенесено ВНИЗ (до оцінок). */}
        {/* Без pr-3 (owner 2026-07-27): правий відступ таблетки джерела має
            дорівнювати лівому відступу категорії — інакше джерело «не дотягує»
            до краю картки. Поля дає p-4 самої кнопки. */}
        <div className="flex items-center gap-1.5 mb-2">
          <WorkKindBadge kind={item.work_kind} />
          <WorkFormatBadge fmt={item.work_format} />
          {item.category_name && (
            <span className="inline-block font-mono text-[0.625rem] tracking-[0.02em] text-primary bg-primary/10 px-2 py-0.5 rounded-full truncate min-w-0">
              {item.category_name}
            </span>
          )}
          {SOURCE_LABEL[item.source] && (
            <span className="inline-block font-mono text-[0.625rem] tracking-[0.04em] text-ink-dim bg-ink/5 px-2 py-0.5 rounded-full shrink-0 ml-auto">
              {SOURCE_LABEL[item.source]}
            </span>
          )}
        </div>

        {/* Факти з тексту вакансії (owner 2026-09-20) — окремим рядом, щоб
            довга категорія не виштовхувала їх із шапки. */}
        <FactPills
          pay={item.pay}
          experience={item.experience}
          location={item.location}
          className="mb-2"
        />

        {/* Summary показуємо ТІЛЬКИ у згорнутій картці — як превʼю-гачок
            для сканування стрічки. Розгорнув → summary зникає, нижче
            ExpandedDetail показує ПОВНИЙ оригінальний текст. Два тексти
            ніколи не висять разом (юзер 2026-06-10). Summary лишається
            тільки в бот-сповіщеннях. */}
        {!expanded && (
          <p className="text-[0.875rem] leading-relaxed text-ink mb-2">{summary}</p>
        )}

        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            {/* Час приходу вакансії — тепер тут, поряд з оцінками (owner
                2026-07-23). */}
            <span className="font-mono text-[0.625rem] text-ink-dim whitespace-nowrap shrink-0">
              {relTime(item.captured_at)}
            </span>
          {item.client_rating_avg !== null && item.client_rating_count > 0 ? (
            <div className="flex items-center gap-2 flex-wrap">
              <StarLine avg={item.client_rating_avg} count={item.client_rating_count} />
              {item.client_reviews_count > 0 && (
                <span className="inline-flex items-center gap-1 font-mono text-[0.625rem] text-primary">
                  ·
                  <Icon name="chat" size={12} color="#FE6B04" />
                  {item.client_reviews_count}{" "}
                  {item.client_reviews_count === 1
                    ? "відгук"
                    : item.client_reviews_count < 5
                      ? "відгуки"
                      : "відгуків"}
                </span>
              )}
            </div>
          ) : item.contact_username ? (
            // Reviews system базується на TG @username — показуємо
            // «Без відгуків» тільки коли є @username замовника.
            // Для Threads / FH (де username немає) — нічого.
            <span className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-ink-dim">
              Без відгуків
            </span>
          ) : (
            <span />
          )}
          </div>
          <Icon name="chevron" size={14} color="rgba(23,20,17,0.45)" className={"text-ink-dim transition-transform duration-200 " + (expanded ? "rotate-180" : "")} />
        </div>
      </button>

      <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          {expanded && <ExpandedDetail vacancyId={item.id} />}
        </div>
      </div>
      </div>
    </div>
  );
}

/**
 * Банер активації промокоду зверху стрічки. Показуємо якщо в localStorage є
 * pending_promo (юзер прийшов по промо-посиланню) і промо ще доступне.
 * Якщо вже використано / невідоме — тихо прибираємо pending_promo.
 */
function GiftFeedBanners() {
  const navigate = useNavigate();
  const giftQuery = useGiftWindow();
  const offers = giftQuery.data?.offers ?? [];
  if (offers.length === 0) return null;
  return (
    <div className="px-5 pt-3 space-y-2">
      {offers.map((o) => (
        <GiftBanner
          key={o.offer}
          gift={o}
          onOpen={() => navigate("/subscription?gift=1")}
        />
      ))}
    </div>
  );
}


function PromoFeedBanner() {
  const navigate = useNavigate();
  const slug =
    typeof localStorage !== "undefined"
      ? localStorage.getItem("pending_promo")
      : null;
  const check = usePromoCheck(slug ?? "", !!slug);

  useEffect(() => {
    // Промо вже використано або невідоме → прибираємо, банер не потрібен.
    if (check.data && !check.data.available) {
      try {
        localStorage.removeItem("pending_promo");
      } catch {
        /* ignore */
      }
    }
  }, [check.data]);

  if (!slug || !check.data || !check.data.available) return null;
  const promo = check.data;

  return (
    <div className="px-5 pt-3">
      <button
        type="button"
        onClick={() => {
          tg()?.HapticFeedback?.impactOccurred?.("light");
          navigate(`/promo/${slug}`);
        }}
        className="flex w-full items-center gap-3 rounded-ios-card bg-surface p-3.5 text-left shadow-card active:scale-[0.99] active:bg-surface-soft transition"
      >
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[0.75rem] bg-primary/10 text-primary">
          <Icon name="gift" size={16} />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block truncate font-display text-[0.875rem] font-bold text-ink leading-tight">
            У тебе є промокод
          </span>
          <span className="mt-0.5 block text-[0.71875rem] text-ink-dim leading-snug">
            {promo.days} днів підписки за 1 ₴ — активуй,
            <br />
            і відкрий повний доступ до inFLOW
          </span>
        </span>
        <Icon name="arrow-right" size={14} color="#FE6B04" className="shrink-0" />
      </button>
    </div>
  );
}

export function FeedPage() {
  const firstPage = useFeedFirstPage(20);
  const subQuery = useMySubscription();
  const subData = subQuery.data;
  const isActive =
    subData && isActiveSubscription(subData) && subData.status === "active";
  const isFrozen =
    subData && isActiveSubscription(subData) && subData.status === "frozen";
  const [items, setItems] = useState<FeedItemOut[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  // Видимий feedback кнопки «Оновити»: refetch інколи миттєвий → юзер не
  // бачив, чи спрацювало (юзер 2026-06-13). Тримаємо спін мінімум ~800мс
  // і даємо success-хаптик у кінці.
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = async () => {
    if (refreshing) return;
    tg()?.HapticFeedback?.impactOccurred?.("light");
    setRefreshing(true);
    const started = Date.now();
    try {
      await firstPage.refetch();
    } finally {
      const wait = Math.max(0, 800 - (Date.now() - started));
      setTimeout(() => {
        setRefreshing(false);
        tg()?.HapticFeedback?.notificationOccurred?.("success");
      }, wait);
    }
  };
  const [expandedId, setExpandedId] = useState<number | null>(null);
  // Переглянуті вакансії (localStorage) — для «Переглянуто» бейджа +
  // затемнення картки. Читаємо на маунті: ловить і відкриття з бота
  // (deep-link /feed/N), і розгортання у стрічці (юзер 2026-06-11).
  const [viewedSet, setViewedSet] = useState<Set<number>>(getViewedVacancies);
  // Refs на DOM карточок — потрібні для anchor-scroll при expand:
  // зберігаємо позицію карточки відносно viewport, після expand
  // компенсуємо scroll щоб карточка ВІЗУАЛЬНО лишалась на тому ж місці
  // (юзер 2026-05-26: «екран має бути статичний, вакансія розкривається вниз»).
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const registerCardRef = (id: number) => (el: HTMLDivElement | null) => {
    if (el) cardRefs.current.set(id, el);
    else cardRefs.current.delete(id);
  };
  const [lastSeenAt] = useState<string | null>(() =>
    localStorage.getItem(FEED_LAST_SEEN_KEY),
  );

  useEffect(() => {
    if (!firstPage.data) return;
    setItems(firstPage.data.items);
    setCursor(firstPage.data.next_cursor);
    setHasMore(firstPage.data.has_more);
  }, [firstPage.data]);

  // Запис «востаннє переглядав N» — щоб бейдж new badge на Home показував свіжі
  useEffect(() => {
    if (firstPage.data && firstPage.data.items.length > 0) {
      const latest = firstPage.data.items[0].captured_at;
      localStorage.setItem(FEED_LAST_SEEN_KEY, latest);
    }
  }, [firstPage.data]);

  const isNew = (iso: string) =>
    Boolean(lastSeenAt && new Date(iso).getTime() > new Date(lastSeenAt).getTime());

  const handleLoadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = await fetchFeedPage(cursor, 20);
      // Дедуп по id (юзер 2026-06-11: «глибоко в стрічці глюки — тап по
      // вакансії моргає, не розкривається»). Корінь: курсорна пагінація
      // на межах сторінок інколи віддає ту саму вакансію двічі → дублікат
      // id → колізія React key={item.id} → миготіння + expandedId
      // матчить дві картки. Додаємо лише нові id.
      setItems((prev) => {
        const seen = new Set(prev.map((x) => x.id));
        const merged = prev.slice();
        for (const it of next.items) {
          if (!seen.has(it.id)) {
            seen.add(it.id);
            merged.push(it);
          }
        }
        return merged;
      });
      setCursor(next.next_cursor);
      setHasMore(next.has_more);
    } finally {
      setLoadingMore(false);
    }
  };

  // Infinite scroll — авто-load коли юзер близько до низу
  useEffect(() => {
    if (!hasMore) return;
    const onScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      if (scrolled >= total - 600 && !loadingMore && cursor) {
        handleLoadMore();
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, cursor, loadingMore]);

  const empty = useMemo(
    () => !firstPage.isLoading && items.length === 0,
    [firstPage.isLoading, items.length],
  );

  if (firstPage.isLoading) return <Spinner />;
  if (firstPage.isError)
    return <CenteredError message={firstPage.error.message} />;

  return (
    <div className="min-h-screen pb-16 overflow-x-hidden">
      {showSearch && <SearchModal onClose={() => setShowSearch(false)} />}
      <ScrollTopButton />
      <div className="px-5 pt-5">
        <BackNav />
      </div>

      <PromoFeedBanner />
      {/* Подарунки (owner 2026-08-11: «промокоди в стрічці теж видно») —
          та сама плашка, що й у підписці; тап веде в підписку до попапа. */}
      <GiftFeedBanners />
      {/* Ретеншн-офер (закреп із таймером) — тільки коли активний (юзер 2026-07-04) */}
      <RetentionOfferBanner wrapperClassName="px-5 pt-3" />

      {/* Hero — єдиний PageHeader (owner 2026-08-11: розміри й відступи
          заголовків мають бути однакові в усіх розділах). */}
      <div className="px-5">
        <PageHeader
          eyebrow="стрічка"
          title="Свіжі вакансії"
          titleLead="Свіжі"
          titleAccent="вакансії."
          scribbleWidth={150}
          // Юзер 2026-05-29: текст показуємо ТІЛЬКИ FREE юзерам — при активній
          // підписці не заповнюємо корисний простір.
          subtitle={
            isActive
              ? undefined
              // ⚠️ Ліміт контактів — пʼять на ТИЖДЕНЬ, а не два на день (owner
              // 2026-09-10 побачив старий текст у стрічці). Правило одне:
              // `vacancy_unlocks` рахує пʼять за тиждень, лічильник у картці
              // «N/5» оновлюється щопонеділка. Міняється ліміт — правити ТУТ,
              // у банері підписки і в промті підтримки.
              : "Все що знайшов бот за останні 7 днів у твоїх сферах. Без підписки контакти можна відкрити пʼять разів на тиждень — з підпискою без обмежень."
          }
          actions={
            <>
              {/* Пошук вакансій (юзер 2026-06-06) */}
              <button
                type="button"
                onClick={() => {
                  tg()?.HapticFeedback?.impactOccurred?.("light");
                  setShowSearch(true);
                }}
                aria-label="Пошук"
                className="grid h-9 w-9 place-items-center rounded-full bg-surface-soft shadow-card-soft active:scale-90 transition"
              >
                <Icon name="search" size={16} color="#FE6B04" />
              </button>
              <button
                type="button"
                onClick={handleRefresh}
                disabled={refreshing}
                aria-label="Оновити"
                className={
                  "grid h-9 w-9 place-items-center rounded-full bg-surface-soft shadow-card-soft active:scale-90 transition " +
                  (refreshing ? "opacity-60 cursor-wait" : "")
                }
              >
                <Icon name="renew" size={16} className={"text-primary " + (refreshing ? "animate-spin" : "")} />
              </button>
            </>
          }
        />
      </div>

      {/* Frozen banner — коли підписка заморожена (юзер 2026-05-22:
          «заморозив → не отримую сповіщення і не бачу новi вакансії
          у feed; зроби красивий блок що підписка заморожена»). */}
      {isFrozen && subData && isActiveSubscription(subData) && (
        <FrozenFeedBanner sub={subData} />
      )}

      {/* Conversion banner для FREE юзерів */}
      {!isFrozen && <FeedUpsellBanner />}

      {/* Юзер 2026-05-29: «прямо в Feed дві кнопки що розкриваються — сфери
          і джерела. Можна швидко скоригувати без переходу в Profile». */}
      <div className="px-5 pt-4">
        <FeedQuickFilters />
      </div>

      {/* Items */}
      <div className="px-5 pt-6">
        {empty ? (
          <div className="rounded-ios-card bg-surface p-8 text-center shadow-card">
            <div className="grid mx-auto mb-3 h-12 w-12 place-items-center rounded-full bg-primary/10">
              <Icon name="search" size={20} color="#FE6B04" />
            </div>
            <div className="font-display text-[0.9375rem] font-semibold text-ink mb-1">
              Поки що тихо
            </div>
            <div className="text-[0.8125rem] text-ink-dim">
              У твоїх сферах за останні 7 днів немає вакансій. Спробуй
              додати більше сфер у Профілі.
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {items.flatMap((item, i) => {
              const dayChanged =
                i === 0 ||
                feedDayKey(item.captured_at) !==
                  feedDayKey(items[i - 1].captured_at);
              // Юзер 2026-06-13: плашку «Сьогодні» не показуємо — лише
              // «Вчора» і старіші дні.
              const showDay =
                dayChanged &&
                feedDayKey(item.captured_at) !== new Date().toDateString();
              const card = (
              <FeedCard
                key={item.id}
                item={item}
                isNew={isNew(item.captured_at)}
                viewed={viewedSet.has(item.id)}
                expanded={expandedId === item.id}
                registerRef={registerCardRef(item.id)}
                onToggle={() => {
                  tg()?.HapticFeedback?.selectionChanged?.();
                  const willExpand = expandedId !== item.id;
                  // Запам'ятовуємо позицію карточки ДО зміни expandedId
                  const el = cardRefs.current.get(item.id);
                  const topBefore = el ? el.getBoundingClientRect().top : null;
                  setExpandedId((cur) => (cur === item.id ? null : item.id));
                  // Після ре-рендера компенсуємо scroll щоб карточка
                  // лишалась на тому ж самому місці у viewport. Працює і
                  // при expand (вже видимий контент не стрибає), і при
                  // collapse (інша карточка згорнулась вище — компенсуємо).
                  if (willExpand && el && topBefore !== null) {
                    requestAnimationFrame(() => {
                      const topAfter = el.getBoundingClientRect().top;
                      const delta = topAfter - topBefore;
                      if (Math.abs(delta) > 1) {
                        window.scrollBy({ top: delta, behavior: "instant" as ScrollBehavior });
                      }
                    });
                  }
                  // Позначаємо «переглянутою» ПІСЛЯ розгортання + scroll-
                  // компенсації (юзер 2026-06-12: непереглянуті картки
                  // «стрибали» — зайвий ре-рендер від setViewedSet збивав
                  // anchor-scroll). localStorage пишемо одразу, візуал —
                  // після анімації.
                  if (willExpand && !viewedSet.has(item.id)) {
                    markVacancyViewed(item.id);
                    setTimeout(
                      () => setViewedSet((prev) => new Set(prev).add(item.id)),
                      360,
                    );
                  }
                }}
              />
              );
              return showDay
                ? [
                    <DayDivider
                      key={`day-${item.id}`}
                      label={feedDayLabel(item.captured_at)}
                    />,
                    card,
                  ]
                : [card];
            })}
          </div>
        )}

        {hasMore && (
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={loadingMore}
            className={
              "mt-5 block w-full rounded-full py-3.5 font-display text-[0.9375rem] font-bold transition shadow-card " +
              (loadingMore
                ? "bg-ink/5 text-ink-dim cursor-not-allowed"
                : "bg-surface text-ink active:bg-surface-soft")
            }
          >
            {loadingMore ? "Завантажую..." : "Завантажити ще"}
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================
// FeedQuickFilters — швидкі фільтри прямо у Feed (юзер 2026-05-29)
// «Дві експандабельні кнопки: сфери і джерела. Поміняв галочки → стрічка
// автоматично оновилась, видалила невідповідні / додала нові вакансії».
// ============================================================

// Порядок 1:1 з профілем (SOURCE_META): канали → чати → Threads →
// Freelancehunt → Finder (юзер 2026-06-11: «щоб було однаково»).
const SOURCE_LABELS: Record<keyof SourcesState, string> = {
  telegram: "Telegram канали",
  chats: "Telegram чати",
  threads: "Threads",
  fhunt: "Freelancehunt",
  finder: "inFLOW Finder (ексклюзив)",
};

function FeedQuickFilters() {
  const [openCats, setOpenCats] = useState(false);
  const [openSrc, setOpenSrc] = useState(false);
  const catsAll = useCategories();
  const profile = useProfileFull();
  const update = useUpdateProfileFull();
  // Юзер 2026-06-06: Freelancehunt + inFLOW Finder — premium джерела.
  // FREE-юзер бачить замочок і не може увімкнути.
  const subQ = useMySubscription();
  const isPaid =
    !!subQ.data &&
    isActiveSubscription(subQ.data) &&
    subQ.data.status === "active";

  // Юзер 2026-06-06: «кнопки сфери/джерела підвантажуються гірше ніж стрічка».
  // Не повертаємо null при loading — рендеримо pill ОДРАЗУ з placeholder
  // лічильником, дані підставляться без «стрибка появи».
  const ready = !!catsAll.data && !!profile.data;
  const allCats = catsAll.data?.items ?? [];

  // Локальний черновик вибору (юзер 2026-06-11: «клацаю 9 сфер підряд —
  // зберігається 2; плюс мисклики»). Корінь: кожен тап слав PUT і блокував
  // плитки через isPending. Тепер: черновик оновлюється СИНХРОННО на кожен
  // тап (миттєвий візуал, копиться без гонки), а PUT летить ОДИН раз з
  // дебаунсом ~450мс після останнього тапу. draft===null → беремо дані сервера.
  const [draftCats, setDraftCats] = useState<number[] | null>(null);
  const [draftSources, setDraftSources] = useState<SourcesState | null>(null);
  const catsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const srcTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selected = new Set(draftCats ?? profile.data?.selected_category_ids ?? []);
  const sources = draftSources ??
    profile.data?.sources ?? {
      telegram: false, chats: false, threads: false, fhunt: false, finder: false,
    };
  // Премиум-джерела (fhunt/finder) для FREE заблоковані — не рахуємо їх як
  // увімкнені, навіть якщо у БД прапор true (юзер 2026-06-08: «вибрано 3,
  // а написано 4»). Лічильник має показувати реально активні джерела.
  const sourcesOn = (Object.keys(sources) as Array<keyof SourcesState>).filter(
    (k) => {
      if (!sources[k]) return false;
      if ((k === "fhunt" || k === "finder") && !isPaid) return false;
      return true;
    },
  ).length;
  const totalSources = Object.keys(sources).length;

  function toggleCategory(id: number) {
    tg()?.HapticFeedback?.selectionChanged?.();
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    const arr = [...next];
    setDraftCats(arr); // миттєвий візуал
    if (catsTimer.current) clearTimeout(catsTimer.current);
    catsTimer.current = setTimeout(() => {
      catsTimer.current = null;
      update.mutate(
        { selected_category_ids: arr },
        {
          // Віддаємо керування серверним даним ТІЛЬКИ якщо юзер не почав
          // новий бёрст (timer===null). Інакше свіжий draft не чіпаємо.
          // При помилці hook робить rollback кешу → draft=null = відкат.
          onSettled: () => {
            if (catsTimer.current === null) setDraftCats(null);
          },
          onError: (e) =>
            showAppAlert(`Помилка збереження: ${(e as Error).message}`),
        },
      );
    }, 450);
  }

  function toggleSource(key: keyof SourcesState) {
    tg()?.HapticFeedback?.selectionChanged?.();
    const next = { ...sources, [key]: !sources[key] };
    setDraftSources(next); // миттєвий візуал
    if (srcTimer.current) clearTimeout(srcTimer.current);
    srcTimer.current = setTimeout(() => {
      srcTimer.current = null;
      update.mutate(
        { sources: next },
        {
          onSettled: () => {
            if (srcTimer.current === null) setDraftSources(null);
          },
          onError: (e) =>
            showAppAlert(`Помилка збереження: ${(e as Error).message}`),
        },
      );
    }, 450);
  }

  // Юзер 2026-05-29: «дві маленькі, акуратні кнопки. Не 30% стрічки».
  // Inline-pill row → клік розкриває контент одразу під рядком.
  return (
    <div>
      <div className="flex gap-2">
        <FilterPill
          label="Сфери"
          counter={ready ? `${selected.size}/${allCats.length}` : "·"}
          open={openCats}
          onClick={() => {
            if (!ready) return;
            setOpenSrc(false);
            setOpenCats((v) => !v);
          }}
        />
        <FilterPill
          label="Джерела"
          counter={ready ? `${sourcesOn}/${totalSources}` : "·"}
          open={openSrc}
          onClick={() => {
            if (!ready) return;
            setOpenCats(false);
            setOpenSrc((v) => !v);
          }}
        />
      </div>

      {/* Розкривний контент — під рядком pill-ів, на повну ширину */}
      <div className={"grid transition-[grid-template-rows] duration-300 ease-out " + (openCats ? "grid-rows-[1fr]" : "grid-rows-[0fr]")}>
        {/* rounded на обгортці-клипі (juzer 2026-06-11: «трикутники фону в
            кутах») — інакше прямокутний overflow-hidden клип не співпадає зі
            скругленням панелі і в кутах проступає фон. mt-2 → у внутр. div. */}
        <div className="overflow-hidden rounded-ios-card mt-2 shadow-card-soft">
          <div className="rounded-ios-card bg-surface p-3 grid grid-cols-2 gap-2">
            {allCats.map((c) => {
              const on = selected.has(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggleCategory(c.id)}
                  className={
                    "text-[0.75rem] px-2.5 py-2.5 min-h-[2.5rem] rounded-full border transition active:scale-[0.97] text-left truncate " +
                    (on
                      ? "bg-primary text-white border-primary"
                      : "bg-bg/40 text-ink border-ink/[0.08]")
                  }
                >
                  {c.name_uk}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className={"grid transition-[grid-template-rows] duration-300 ease-out " + (openSrc ? "grid-rows-[1fr]" : "grid-rows-[0fr]")}>
        <div className="overflow-hidden rounded-ios-card mt-2 shadow-card-soft">
          <div className="rounded-ios-card bg-surface p-3 space-y-2">
            {(Object.keys(SOURCE_LABELS) as Array<keyof SourcesState>).map((key) => {
              const on = sources[key];
              // Premium-джерела: fhunt + finder. FREE → замок, не toggle.
              const isPremiumSource = key === "fhunt" || key === "finder";
              const locked = isPremiumSource && !isPaid;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={
                    locked
                      ? () => {
                          tg()?.HapticFeedback?.notificationOccurred?.("warning");
                          showAppAlert(
                            "Оформи підписку, щоб отримувати вакансії з цього джерела.",
                            { title: "Джерело під замком", emoji: "🔒" },
                          );
                        }
                      : () => toggleSource(key)
                  }
                  className="w-full flex items-center gap-3 px-3 py-2.5 min-h-[2.75rem] rounded-xl bg-bg/40 active:bg-bg/60 transition text-left"
                >
                  <div className={"flex-1 text-[0.8125rem] " + (locked ? "text-ink-dim" : "text-ink")}>
                    {SOURCE_LABELS[key]}
                  </div>
                  {locked ? (
                    <Icon name="lock" size={14} color="#A8A29C" />
                  ) : (
                    <div
                      className={
                        "relative h-5 w-9 shrink-0 rounded-full transition " +
                        (on ? "bg-primary" : "bg-ink/15")
                      }
                    >
                      <div
                        className={
                          "absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-md transition-all " +
                          (on ? "left-[1.125rem]" : "left-0.5")
                        }
                      />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterPill({
  label,
  counter,
  open,
  onClick,
}: {
  label: string;
  counter: string;
  open: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "flex-1 flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition active:scale-[0.98] " +
        (open
          ? "bg-primary/10 border-primary/40"
          : "bg-surface-soft border-ink/[0.08] shadow-card-soft")
      }
    >
      <div className="font-display text-[0.75rem] font-semibold text-ink">
        {label}
      </div>
      <div className="font-mono text-[0.65625rem] text-ink-dim tabular-nums ml-auto">
        {counter}
      </div>
      <Icon name="chevron" size={9} color="rgba(23,20,17,0.45)" className={"text-ink-dim transition-transform duration-200 " + (open ? "rotate-180" : "")} />
    </button>
  );
}

// ============================================================
// SearchModal — пошук вакансій по всій базі за півроку (owner 2026-09-09;
// раніше вікно було 30 днів, разом зі строком зберігання)
// ============================================================

// Плаваюча кнопка «вгору» — зʼявляється коли стрічку прокрутили вниз
// (юзер 2026-06-11: «листав багато вакансій — одною кнопкою повернутися
// нагору»). Стрічка скролиться на window.
function ScrollTopButton() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 800);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!show) return null;
  // Портал у body: інакше кнопка сидить усередині .page-transition, який
  // тримає transform (will-change/fill both) → containing block для fixed,
  // і кнопка позиціонувалась відносно нього, а не viewport (юзер 2026-06-11:
  // «не побачив кнопку нагору»). Через портал — справжній fixed до екрана.
  return createPortal(
    <button
      type="button"
      onClick={() => {
        tg()?.HapticFeedback?.impactOccurred?.("light");
        window.scrollTo({ top: 0, behavior: "smooth" });
      }}
      aria-label="Нагору"
      className="fixed right-4 z-[60] grid h-11 w-11 place-items-center rounded-full bg-primary text-white shadow-card active:scale-90 transition"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 20px)" }}
    >
      <Icon name="arrow-right" size={16} color="#FFFFFF" className="-rotate-90" />
    </button>,
    document.body,
  );
}

const SEARCH_HISTORY_KEY = "search_history_v1";
function loadSearchHistory(): string[] {
  try {
    const raw = localStorage.getItem(SEARCH_HISTORY_KEY);
    const arr = raw ? JSON.parse(raw) : null;
    if (Array.isArray(arr))
      return arr.filter((x) => typeof x === "string").slice(0, 5);
  } catch {
    /* ignore */
  }
  return [];
}

function SearchModal({ onClose }: { onClose: () => void }) {
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  // Історія останніх 5 пошуків (юзер 2026-06-11) — localStorage, показуємо
  // на стартовому екрані пошуку, тап → повторний пошук.
  const [history, setHistory] = useState<string[]>(loadSearchHistory);
  // Переглянуті вакансії — бейдж/затемнення і в результатах пошуку.
  const [viewedSet, setViewedSet] = useState<Set<number>>(getViewedVacancies);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  // Юзер 2026-06-06: submit-based (не live). Акумулюємо сторінки + total.
  const [items, setItems] = useState<FeedItemOut[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searched, setSearched] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const registerCardRef = (id: number) => (el: HTMLDivElement | null) => {
    if (el) cardRefs.current.set(id, el);
    else cardRefs.current.delete(id);
  };

  // Лочимо скрол body поки відкритий пошук — інакше iOS «гумить» сторінку
  // під fixed-модалкою (особливо з відкритою клавіатурою: каретка/контент
  // їздять вгору-вниз). Юзер 2026-06-11.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const runSearch = async (term?: string) => {
    const t = (term ?? input).trim();
    if (t.length < 2) return;
    if (term !== undefined) setInput(term);
    tg()?.HapticFeedback?.impactOccurred?.("light");
    setQuery(t);
    // Зберігаємо в історію (дедуп без регістру, максимум 5, свіже зверху).
    setHistory((prev) => {
      const next = [
        t,
        ...prev.filter((x) => x.toLowerCase() !== t.toLowerCase()),
      ].slice(0, 5);
      try {
        localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
    setSearched(true);
    setLoading(true);
    setExpandedId(null);
    try {
      const page = await fetchSearchPage(t, 0);
      setItems(page.items);
      setTotal(page.total ?? page.items.length);
      setHasMore(page.has_more);
    } catch {
      setItems([]);
      setTotal(0);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const page = await fetchSearchPage(query, items.length);
      setItems((prev) => [...prev, ...page.items]);
      setHasMore(page.has_more);
    } finally {
      setLoadingMore(false);
    }
  };

  // Infinite scroll у scroll-контейнері модалки.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !hasMore) return;
    const onScroll = () => {
      if (
        el.scrollTop + el.clientHeight >= el.scrollHeight - 400 &&
        !loadingMore
      ) {
        loadMore();
      }
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadingMore, items.length, query]);

  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col overflow-hidden bg-bg">
      <AuraBackground />
      {/* Єдиний хедер — логотип на тому ж місці, що й на головній
          (StandaloneHeader = topInset + 20). Справа «Назад» у стрічку. */}
      <StandaloneHeader
        right={
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 items-center gap-1.5 rounded-full bg-surface-soft px-3 font-display text-[0.75rem] font-semibold text-ink shadow-card-soft active:scale-95 transition"
          >
            <Icon name="arrow-right" size={14} className="rotate-180" color="#FE6B04" />
            Назад
          </button>
        }
      />
      {/* Поле пошуку — окремий рядок під хедером. */}
      <div className="relative z-[1] px-5 pb-3 border-b border-ink/[0.06]">
        <div className="flex items-center gap-3">
          {/* Юзер 2026-06-06: «не дублюй лупу в полі + в кнопці. Зроби одне
              ціле». Прибрав окрему кнопку — лупа ЗЛІВА у полі клікабельна
              і запускає пошук (+ Enter). Одна лупа, чисто. */}
          <form
            onSubmit={(e) => { e.preventDefault(); runSearch(); }}
            className="flex-1 flex items-center gap-2 rounded-full bg-surface shadow-card-soft pl-2 pr-4 py-2 min-w-0"
          >
            <button
              type="submit"
              disabled={input.trim().length < 2}
              aria-label="Знайти"
              className="grid h-8 w-8 place-items-center rounded-full bg-primary active:scale-90 transition disabled:opacity-40 shrink-0"
            >
              <Icon name="search" size={16} color="#fff" />
            </button>
            <input
              type="text"
              autoFocus
              enterKeyHint="search"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Слово, @username, посилання…"
              className="flex-1 min-w-0 bg-transparent outline-none text-[0.875rem] text-ink placeholder:text-ink-dim"
            />
            {input && (
              <button type="button" onClick={() => { setInput(""); setQuery(""); setSearched(false); setItems([]); }} className="text-ink-dim active:text-ink shrink-0">
                <Icon name="plus" size={14} className="rotate-45" />
              </button>
            )}
          </form>
        </div>
      </div>

      {/* Body */}
      <div ref={scrollRef} className="relative z-[1] flex-1 overflow-y-auto overscroll-contain px-5 py-4">
        {!searched ? (
          <>
          {/* Останні пошуки — тап повторює запит (юзер 2026-06-11). */}
          {history.length > 0 && (
            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-[0.6875rem] uppercase tracking-[0.1em] text-ink-dim">
                  Останні пошуки
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setHistory([]);
                    try {
                      localStorage.removeItem(SEARCH_HISTORY_KEY);
                    } catch {
                      /* ignore */
                    }
                  }}
                  className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-ink-dim active:text-ink"
                >
                  Очистити
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {history.map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => runSearch(h)}
                    className="flex items-center gap-1.5 rounded-full bg-surface shadow-card-soft px-3 py-1.5 text-[0.78125rem] text-ink active:scale-95 transition max-w-full"
                  >
                    <Icon name="search" size={12} color="#FE6B04" />
                    <span className="truncate">{h}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="rounded-ios-card bg-surface shadow-card-soft p-4">
            <div className="font-display text-[0.875rem] font-bold text-ink mb-2">
              Як працює пошук 🔍
            </div>
            <ul className="space-y-1.5 text-[0.8125rem] text-ink-dim leading-relaxed">
              <li>• Шукає по <b className="text-ink">всій базі</b> вакансій за останні півроку (не лише твої сфери).</li>
              <li>• Будь-яке слово або його частина з тексту вакансії, AI-опису, юзернейму чи посилання.</li>
              <li>• Приклади: <span className="font-mono text-ink">SMM</span>, <span className="font-mono text-ink">дизайнер</span>, <span className="font-mono text-ink">@username</span>, <span className="font-mono text-ink">Польща</span>.</li>
              <li>• Введи запит і натисни <b className="text-ink">Знайти</b> (або Enter).</li>
            </ul>
          </div>
          </>
        ) : loading ? (
          <div className="pt-10"><Spinner /></div>
        ) : items.length === 0 ? (
          <div className="rounded-ios-card bg-surface p-8 text-center shadow-card">
            <div className="grid mx-auto mb-3 h-12 w-12 place-items-center rounded-full bg-primary/10">
              <Icon name="search" size={20} color="#FE6B04" />
            </div>
            <div className="font-display text-[0.9375rem] font-semibold text-ink mb-1">
              Нічого не знайдено
            </div>
            <div className="text-[0.8125rem] text-ink-dim">
              За запитом «{query}» немає вакансій за останні півроку.
            </div>
          </div>
        ) : (
          <div>
            <div className="font-mono text-[0.6875rem] uppercase tracking-[0.1em] text-ink-dim mb-3">
              Знайдено: {total}
            </div>
            <div className="space-y-3">
              {items.map((item) => (
                <FeedCard
                  key={item.id}
                  item={item}
                  isNew={false}
                  viewed={viewedSet.has(item.id)}
                  expanded={expandedId === item.id}
                  registerRef={registerCardRef(item.id)}
                  onToggle={() => {
                    tg()?.HapticFeedback?.selectionChanged?.();
                    const willExpand = expandedId !== item.id;
                    const el = cardRefs.current.get(item.id);
                    const topBefore = el ? el.getBoundingClientRect().top : null;
                    setExpandedId((cur) => (cur === item.id ? null : item.id));
                    if (willExpand && el && topBefore !== null) {
                      requestAnimationFrame(() => {
                        const topAfter = el.getBoundingClientRect().top;
                        const delta = topAfter - topBefore;
                        if (Math.abs(delta) > 1 && scrollRef.current) {
                          scrollRef.current.scrollBy({
                            top: delta,
                            behavior: "instant" as ScrollBehavior,
                          });
                        }
                      });
                    }
                    // Позначка «переглянуто» — після розгортання (анти-стрибок).
                    if (willExpand && !viewedSet.has(item.id)) {
                      markVacancyViewed(item.id);
                      setTimeout(
                        () => setViewedSet((prev) => new Set(prev).add(item.id)),
                        360,
                      );
                    }
                  }}
                />
              ))}
            </div>
            {hasMore && (
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className={
                  "mt-5 block w-full rounded-full py-3.5 font-display text-[0.9375rem] font-bold transition shadow-card " +
                  (loadingMore
                    ? "bg-ink/5 text-ink-dim cursor-not-allowed"
                    : "bg-surface text-ink active:bg-surface-soft")
                }
              >
                {loadingMore ? "Завантажую..." : "Завантажити ще"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
